import { useMemo } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { uploadFileToS3 } from '@/helpers';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useCreateGcrs, useGcrFormOptions } from '../api';
import { GcrFormValues } from '../validations';
import { defaultFormValues, GcrForm } from './gcr-form';

interface CreateGcrDialogProps {
  isOpen: boolean;
  typeId?: string;
  sizeId?: string;
  printerId?: string;
  colorId?: string;
  onSuccess: () => Promise<any>;
  onOpenChange: (open: boolean) => void;
}

export function CreateGcrDialog({
  isOpen,
  onOpenChange,
  typeId,
  sizeId,
  printerId,
  colorId,
  onSuccess,
}: CreateGcrDialogProps) {
  const { optionsData } = useGcrFormOptions();
  const { createGcrMutation, uploadUrlDataMutation } = useCreateGcrs();

  const isLoading =
    createGcrMutation.isPending || uploadUrlDataMutation.isPending;

  const saveGcr = async (values: GcrFormValues) => {
    try {
      let key: string | undefined;

      if (values.filePublicUrl instanceof File) {
        const { url, key: uploadKey } =
          await uploadUrlDataMutation.mutateAsync();
        await uploadFileToS3({ file: values.filePublicUrl, uploadUrl: url });
        key = uploadKey;
      }

      await createGcrMutation.mutateAsync({
        id: crypto.randomUUID(),
        name: values.name,
        combinations: values.combinations.map((combination) => ({
          colorId: values.colorId,
          printerId: values.printerId,
          typeId: combination.typeId,
          sizeId: combination.sizeId,
        })),
        ...(key && { key }),
      });

      await onSuccess();
      toast.success(`GCR file “${values.name}” added successfully!`);
      onOpenChange(false);
    } catch (error) {
      toast.error(error?.message || 'Uh oh! Something went wrong.');
    }
  };

  const defaultValues = useMemo(
    () => ({
      ...defaultFormValues,
      colorId: colorId ?? '',
      printerId: printerId ?? '',
      combinations:
        typeId && sizeId ? [{ typeId, sizeId }] : defaultFormValues.combinations,
    }),
    [typeId, sizeId, printerId, colorId]
  );

  return (
    <Dialog onOpenChange={onOpenChange} open={isOpen}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New GCR File</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <GcrForm
          denseMode
          onSubmit={saveGcr}
          options={optionsData}
          defaultValues={defaultValues}
          formClassName="flex flex-col-reverse"
        >
          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button
                variant="outline"
                className="w-[130px]"
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              isLoading={isLoading}
              className="w-[130px]"
              data-testid="save-button"
            >
              Save changes
            </Button>
          </DialogFooter>
        </GcrForm>
      </DialogContent>
    </Dialog>
  );
}
